import Alpine from 'alpinejs';
import { normalize } from '../utils/helpers.js';
import { fetchGeocodingSuggestions } from '../utils/mapboxGeocoding.js';

const MAX_ITEMS = 10;

Alpine.data('whereAutocomplete', (config = {}) => ({
    craftLocations: config.craftLocations || [],
    suggestions: [],
    rentalSuggestions: [],
    loading: false,
    loadingRentals: false,
    highlightedIndex: -1,
    abortController: null,
    rentalAbortController: null,
    _debounceTimer: null,
    _isSelecting: false,
    _pendingSubmit: false,

    init() {
        this.$root.addEventListener('autocomplete:select-first', () => {
            this._pendingSubmit = true;
            this._selectHighlighted();
        });
    },

    openPopover() {
        if (this._isSelecting) return;

        // Don't open on focus/click if the field already has content —
        // only handleInput (actual typing) should reopen the popover.
        const query = Alpine.store('search').where;
        if (query && query.length > 0) return;

        const popoverKey = this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        if (popoverKey) Alpine.store('popovers').open(popoverKey);
    },

    handleInput() {
        if (this._isSelecting) return;

        const popoverKey = this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        if (popoverKey) Alpine.store('popovers').open(popoverKey);

        Alpine.store('search').whereData = null;

        clearTimeout(this._debounceTimer);
        const query = Alpine.store('search').where;

        if (!query || query.length < 2) {
            this.suggestions = [];
            this.rentalSuggestions = [];
            this.highlightedIndex = -1;
            return;
        }

        this._debounceTimer = setTimeout(() => this._fetchSuggestions(query), 300);
    },

    // --- Computed getters ---

    get filteredCraftEntries() {
        const query = Alpine.store('search').where;
        return this.craftLocations.filter(
            (entry) => Alpine.store('search').matches(entry.title, query),
        );
    },

    get filteredMarkets() {
        return this.filteredCraftEntries.filter((e) => e.type === 'market');
    },

    get filteredDestinations() {
        return this.filteredCraftEntries.filter((e) => e.type === 'destination');
    },

    get cappedMapboxSuggestions() {
        const craftCount = this.filteredCraftEntries.length;
        const mapboxSlots = Math.max(0, MAX_ITEMS - craftCount);
        return this.suggestions.slice(0, mapboxSlots);
    },

    get totalItems() {
        return (
            this.filteredCraftEntries.length +
            this.cappedMapboxSuggestions.length +
            this.rentalSuggestions.length
        );
    },

    // --- Fetch methods ---

    async _fetchSuggestions(query) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        // Build dedup set from craft locations
        const craftTitles = new Set(
            this.craftLocations.map((e) => normalize(e.title)),
        );

        this.loading = true;
        try {
            const results = await fetchGeocodingSuggestions(
                query,
                this.abortController.signal,
            );
            this.suggestions = results.filter(
                (s) => !craftTitles.has(normalize(s.name)),
            );

            // Check if we need rental suggestions to fill remaining slots
            const craftCount = this.filteredCraftEntries.length;
            const mapboxCount = this.cappedMapboxSuggestions.length;
            const remaining = MAX_ITEMS - craftCount - mapboxCount;

            if (remaining > 0) {
                this._fetchRentalSuggestions(query, remaining);
            } else {
                this.rentalSuggestions = [];
            }
        } catch (e) {
            if (e.name !== 'AbortError') console.error('Geocoding error:', e);
        } finally {
            this.loading = false;
        }
    },

    async _fetchRentalSuggestions(query, limit) {
        if (this.rentalAbortController) this.rentalAbortController.abort();
        this.rentalAbortController = new AbortController();
        this.loadingRentals = true;

        try {
            const encoded = encodeURIComponent(query.trim());
            const response = await fetch(
                `/actions/guesty/search/autocomplete?q=${encoded}&limit=${limit}`,
                { signal: this.rentalAbortController.signal },
            );

            if (!response.ok) {
                this.rentalSuggestions = [];
                return;
            }

            const data = await response.json();
            this.rentalSuggestions = data.success ? data.results : [];
        } catch (e) {
            if (e.name !== 'AbortError')
                console.error('Rental autocomplete error:', e);
            this.rentalSuggestions = [];
        } finally {
            this.loadingRentals = false;
        }
    },

    // --- Selection methods ---

    selectCraftEntry(title, lat, lng, slug) {
        const store = Alpine.store('search');
        this._isSelecting = true;

        store.where = title;
        store.whereData =
            lat != null && lng != null
                ? { lat, lng, slug }
                : { lat: null, lng: null, slug };

        this.suggestions = [];
        this.rentalSuggestions = [];
        this.highlightedIndex = -1;
        this._closePopover();
        this.$refs.whereInput?.blur();

        this.$nextTick(() => {
            this._isSelecting = false;
            if (this._pendingSubmit) {
                this._pendingSubmit = false;
                store.submitSearch();
            }
        });
    },

    selectMapboxSuggestion(suggestion) {
        const store = Alpine.store('search');
        this._isSelecting = true;

        store.where = suggestion.fullName;
        store.whereData = {
            lat: suggestion.lat,
            lng: suggestion.lng,
        };

        this.highlightedIndex = -1;
        this._closePopover();

        setTimeout(() => {
            this.suggestions = [];
            this.rentalSuggestions = [];
            this._isSelecting = false;
            if (this._pendingSubmit) {
                this._pendingSubmit = false;
                store.submitSearch();
            }
        }, 50);
    },

    selectRentalSuggestion(rental) {
        Alpine.store('search').where = rental.name;
        this.suggestions = [];
        this.rentalSuggestions = [];
        this.highlightedIndex = -1;
        this._closePopover();

        window.location.href = '/listing/' + rental.slug;
    },

    // --- Keyboard navigation ---

    handleKeydown(event) {
        const popoverKey =
            this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        const isOpen =
            popoverKey && Alpine.store('popovers').isOpen(popoverKey);

        if (!isOpen) return;

        const total = this.totalItems;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.highlightedIndex =
                (this.highlightedIndex + 1) % Math.max(total, 1);
            this._scrollToHighlighted();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.highlightedIndex =
                this.highlightedIndex <= 0
                    ? Math.max(total - 1, 0)
                    : this.highlightedIndex - 1;
            this._scrollToHighlighted();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            this._selectHighlighted();
        } else if (event.key === 'Escape') {
            this.suggestions = [];
            this.rentalSuggestions = [];
            this.highlightedIndex = -1;
        }
    },

    _selectHighlighted() {
        const craftEntries = this.filteredCraftEntries;
        const craftCount = craftEntries.length;
        const mapboxItems = this.cappedMapboxSuggestions;
        const mapboxCount = mapboxItems.length;
        const rentals = this.rentalSuggestions;

        const idx = this.highlightedIndex >= 0 ? this.highlightedIndex : 0;
        const total = craftCount + mapboxCount + rentals.length;

        if (total === 0) return;

        if (idx < craftCount) {
            const entry = craftEntries[idx];
            this.selectCraftEntry(entry.title, entry.lat, entry.lng, entry.slug);
        } else if (idx < craftCount + mapboxCount) {
            const mapboxIdx = idx - craftCount;
            this.selectMapboxSuggestion(mapboxItems[mapboxIdx]);
        } else {
            const rentalIdx = idx - craftCount - mapboxCount;
            if (rentals[rentalIdx]) {
                this.selectRentalSuggestion(rentals[rentalIdx]);
            }
        }
    },

    _scrollToHighlighted() {
        this.$nextTick(() => {
            const el = this.$root.querySelector('.is-highlighted');
            const container = this.$root.querySelector('.form-popover--where');
            if (!el || !container) return;

            const elRect = el.getBoundingClientRect();
            const cRect = container.getBoundingClientRect();

            if (elRect.bottom > cRect.bottom) {
                container.scrollTop += elRect.bottom - cRect.bottom;
            } else if (elRect.top < cRect.top) {
                container.scrollTop -= cRect.top - elRect.top;
            }
        });
    },

    _closePopover() {
        const popoverKey =
            this.$root.closest('[data-popover-key]')?.dataset?.popoverKey;
        if (popoverKey) Alpine.store('popovers').close(popoverKey);
    },

    destroy() {
        clearTimeout(this._debounceTimer);
        if (this.abortController) this.abortController.abort();
        if (this.rentalAbortController) this.rentalAbortController.abort();
    },
}));
